"use client";

import { Fragment, type ReactNode } from "react";
import Link from "next/link";
import { matchPath, SITE_URL } from "@/lib/chat/routes";

/**
 * Renders an answer, turning references to this site's pages into links.
 *
 * The model writes a bare path — `/solutions/ai-automation` — because a path is
 * something the route registry can check. This turns each one it recognises
 * into the full address, as a link the visitor can activate.
 *
 * A path is linked only when `matchPath` resolves it to a *static* page. That
 * rules out two things at once: an address the site does not have, which stays
 * plain text exactly as written, and a dynamic route like `/blog/[slug]`, where
 * the pattern matches but the slug itself is unverifiable and could 404.
 *
 * A home-page section is written as an anchor — `/#packages` — and is linked
 * only when the registry holds that exact anchor. Any other fragment stays
 * plain text rather than becoming a link to the top of the home page.
 *
 * Markdown links are unwrapped rather than rendered as markdown. The answer is
 * model output, so it is never parsed as markup — there is no path here by
 * which it could produce anything but text and internal links.
 */

/** `[label](/path)` or `[label](https://haintz.dev/path)` → the target alone. */
const MARKDOWN_LINK = /\[([^\]\n]*)\]\((\S+?)\)/g;

/** A site path or home-page anchor, optionally with the domain in front. */
const SITE_REFERENCE = new RegExp(
  `(?:https?://(?:www\\.)?haintz\\.dev)?(/#[a-z][a-z0-9-]*|/[a-z0-9][a-z0-9-]*(?:/[a-z0-9][a-z0-9-]*)*)`,
  "gi",
);

/** Whether this path is one the site definitely serves. */
function linkablePath(path: string): string | null {
  const entry = matchPath(path);
  // An anchor counts only as itself: `matchPath` reads an unregistered one as
  // the page it sits on, which is not what the text claimed.
  if (path.includes("#")) return entry?.path === path ? path : null;
  // `pattern` means a dynamic route: the shape matches but the slug does not
  // have to exist, so it is not safe to offer as a link.
  return entry && !entry.pattern ? entry.path : null;
}

export default function AnswerText({ text }: { text: string }) {
  // Unwrap any markdown link to its target first, so the scan below sees one
  // shape rather than two.
  const flattened = text.replace(MARKDOWN_LINK, (match, _label, target) =>
    linkablePath(String(target).replace(/^https?:\/\/(?:www\.)?haintz\.dev/i, "") || "/")
      ? String(target)
      : match,
  );

  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const match of flattened.matchAll(SITE_REFERENCE)) {
    const path = linkablePath(match[1]);
    if (!path || match.index === undefined) continue;

    if (match.index > cursor) parts.push(flattened.slice(cursor, match.index));
    parts.push(
      <Link
        key={`${match.index}-${path}`}
        href={path}
        className="text-mint-text underline underline-offset-2 transition-colors hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint-text"
      >
        {`${SITE_URL}${path === "/" ? "" : path}`}
      </Link>,
    );
    cursor = match.index + match[0].length;
  }

  if (cursor === 0) return <span className="whitespace-pre-wrap">{text}</span>;
  if (cursor < flattened.length) parts.push(flattened.slice(cursor));

  return (
    <span className="whitespace-pre-wrap">
      {parts.map((part, i) => (
        <Fragment key={i}>{part}</Fragment>
      ))}
    </span>
  );
}
